/**
 * Basecamp Todo Info Pane
 * Shows metadata and actions for a Basecamp todo
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Calendar, Users, User, FileText, Clock, CheckCircle2, Info } from 'lucide-react';
import { basecampApi } from '@/lib/basecamp-api';
import { dueDateStatus, parseLocalDate } from '@/lib/utils';
import type { BasecampTodo } from '@/types/basecamp';
import { PersonLink } from '@/components/features/person-link';

interface BasecampTodoInfoPaneProps {
  todoId: string;
  projectId?: string;
  todo?: BasecampTodo | null; // Pre-loaded todo data (optional, will fetch if not provided)
  onClose: () => void;
}

export function BasecampTodoInfoPane({ todoId, projectId, todo: propTodo, onClose }: BasecampTodoInfoPaneProps) {
  const [todo, setTodo] = useState<BasecampTodo | null>(propTodo || null);
  const [isLoading, setIsLoading] = useState(!propTodo);

  useEffect(() => {
    // If todo is provided as prop, use it and skip fetch
    if (propTodo) {
      setTodo(propTodo);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch the todo
    if (!projectId) return;
    
    const loadTodo = async () => {
      try {
        setIsLoading(true);
        const todoData = await basecampApi.getTodoById(projectId, todoId);
        setTodo(todoData);
      } catch (err) {
        console.error('[BasecampTodoInfoPane] Error loading todo:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadTodo();
  }, [projectId, todoId, propTodo]);

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        {/* Header - Match SecondaryPanelContent style */}
        <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Todo Info
            </h2>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Close" aria-label="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <Clock className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!todo) {
    return (
      <div className="h-full flex flex-col">
        {/* Header - Match SecondaryPanelContent style */}
        <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Todo Info
            </h2>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" className="h-7 w-7" title="Close" aria-label="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <p className="text-sm text-muted-foreground">Todo not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Match SecondaryPanelContent style */}
      <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Info className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Todo Info
          </h2>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Close" aria-label="Close" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Todo Title */}
        <div className="mb-4">
          <h3 className="text-sm font-medium mb-2">{todo.title}</h3>
          <div className="flex flex-wrap gap-2">
            {todo.completed && (
              <Badge variant="default" className="bg-green-500/10 text-green-500 border-green-500/20">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Completed
              </Badge>
            )}
            {todo.due_on && dueDateStatus(todo.due_on).isOverdue && !todo.completed && (
              <Badge variant="destructive">Overdue</Badge>
            )}
          </div>
        </div>
        {/* Metadata */}
        <div className="space-y-3">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Details
          </div>
          
          {todo.due_on && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Due:</span>
              <span>{parseLocalDate(todo.due_on).toLocaleDateString()}</span>
            </div>
          )}
          
          {todo.comments_count !== undefined && (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Comments:</span>
              <span>{todo.comments_count}</span>
            </div>
          )}
          
          {todo.created_at && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Created:</span>
              <span>{new Date(todo.created_at).toLocaleDateString()}</span>
            </div>
          )}
          
          {todo.creator && (
            <div className="flex items-center gap-2 text-sm">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Created by:</span>
              <span>{todo.creator.name}</span>
            </div>
          )}
          
          {todo.assignees && todo.assignees.length > 0 && (
            <div className="flex items-start gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <span className="text-muted-foreground">Assignees:</span>
                <div className="mt-1 space-y-1">
                  {todo.assignees.map(assignee => (
                    <div key={assignee.id} className="flex items-center gap-2">
                      {assignee.avatar_url && (
                        <img 
                          src={assignee.avatar_url} 
                          alt={assignee.name}
                          className="h-5 w-5 rounded-full"
                        />
                      )}
                      <PersonLink
                        name={assignee.name || 'Unknown'}
                        basecampPersonId={assignee.id}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {todo.parent && (
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">List:</span>
              <span>{todo.parent.title}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

